import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound, useRouter } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import * as z from "zod"

import type { ExtendedClient } from "@repo/supabase/database-types"
import { PagePanel } from "@repo/ui/components/ui/page-panel"
import { useIsMobile } from "@repo/ui/hooks/use-mobile"
import { cn } from "@repo/ui/lib/utils"
import { pick } from "@repo/utils/collections"
import { getExtendedBusinessEntitiesQueryOptions } from "~/modules/business-entities/business-entities.queries"
import { getExtendedClientsQueryOptions } from "~/modules/clients/clients.queries"
import { ClientsSearchList } from "~/modules/clients/components/clients-search-list"
import { CreateClientModal } from "~/modules/clients/components/create-client-modal"
import { useFeatureConfigs } from "~/modules/system-config/hooks/use-feature-configs"

const searchSchema = z.object({
  be_slug: z.string().optional(),
  client_slug: z.string().optional(),
})

export const Route = createFileRoute("/_protected-layout/_os-layout/clients/")({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["be_slug", "client_slug"]),
})

function RouteComponent() {
  const { isClientManagementOn } = useFeatureConfigs()

  if (!isClientManagementOn) {
    throw notFound()
  }

  const router = useRouter()
  const isMobile = useIsMobile()
  const { be_slug, client_slug } = Route.useSearch()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const { data: businessEntitiesData } = useSuspenseQuery(
    getExtendedBusinessEntitiesQueryOptions(),
  )

  const selectedBusinessEntity = useMemo(
    () =>
      businessEntitiesData.businessEntities.find(
        (entity) => entity.slug === be_slug,
      ) ?? null,
    [businessEntitiesData, be_slug],
  )

  const businessEntityFilterOptions = useMemo(() => {
    return businessEntitiesData.businessEntities.map((entity) => ({
      label: entity.name,
      value: entity.slug,
    }))
  }, [businessEntitiesData])

  const { data: clientsData, isLoading: isClientsLoading } = useQuery(
    getExtendedClientsQueryOptions({
      filters: {
        page: 1,
        page_size: 1000, // TODO: This should be replaced with an infinite scroll list (SearchList needs to be updated to support this)
        columnFilters: selectedBusinessEntity?.unit_id
          ? [
              {
                id: "business_entity_unit_ids",
                value: [selectedBusinessEntity.unit_id],
              },
            ]
          : undefined,
      },
    }),
  )

  const selectedClient = useMemo(() => {
    const selected = clientsData?.clients.find(
      (client) => client.slug === client_slug,
    )

    if (selected) {
      return selected
    }

    return null
  }, [clientsData, client_slug])

  const onSelectBusinessEntity = (businessEntitySlug: string | null) => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        be_slug: businessEntitySlug ?? undefined,
      }),
    })
  }

  const onSelectClient = (client: ExtendedClient | null) => {
    if (!client) {
      return
    }

    router.navigate({
      to: "/clients/$clientSlug/overview",
      params: { clientSlug: client.slug },
      search: (prev) => prev,
    })
  }

  const onAddClient = () => {
    setIsCreateModalOpen(true)
  }

  // Auto-redirect to first client's overview if no client is selected but clients are available
  useEffect(() => {
    if (
      !client_slug &&
      clientsData?.clients &&
      clientsData.clients.length > 0 &&
      !isClientsLoading
    ) {
      const firstClient = clientsData.clients[0]
      router.navigate({
        to: "/clients/$clientSlug/overview",
        params: { clientSlug: firstClient.slug },
        search: (prev) => prev,
        replace: true,
      })
    }
  }, [client_slug, clientsData, isClientsLoading, router])

  return (
    <PagePanel title="Clients">
      <div className="flex h-full flex-col gap-4 md:flex-row">
        <div
          className={cn("block md:block md:w-1/3 lg:w-1/4", {
            hidden: isMobile,
          })}
        >
          <ClientsSearchList
            clients={clientsData?.clients ?? []}
            selectedClientSlug={selectedClient?.slug ?? null}
            onSelectClient={onSelectClient}
            onAddClient={onAddClient}
            filterOptions={businessEntityFilterOptions}
            filterValue={selectedBusinessEntity?.slug ?? null}
            setFilterValue={onSelectBusinessEntity}
            filterPlaceholder="Select a business entity"
            isLoading={isClientsLoading}
          />
        </div>
        <div className="block flex-1 md:w-2/3 lg:w-3/4">
          <div className="glass-darker flex h-full flex-col rounded-lg" />
        </div>
      </div>

      <CreateClientModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
    </PagePanel>
  )
}
